import type { GraphCore, GraphNodeRecord } from "../data/buildGraphData";

export type GraphSelection = {
  type: "overview" | "stage" | "question" | "module" | "submodule" | "node" | "all";
  id: string;
};

export function parseGraphHash(hash: string): GraphSelection {
  const params = new URLSearchParams(hash.replace(/^#/, ""));
  const stage = params.get("stage");
  if (stage && /^\d+$/.test(stage)) return { type: "stage", id: `stage-${stage}` };
  const question = params.get("question");
  if (question && /^Q\d{2}$/.test(question)) return { type: "question", id: question };
  const module = params.get("module");
  if (module && /^\d+$/.test(module)) return { type: "module", id: `module-${module}` };
  const submodule = params.get("submodule");
  if (submodule && /^\d+\.\d+$/.test(submodule)) {
    return { type: "submodule", id: `submodule-${submodule}` };
  }
  const node = params.get("node");
  if (node && /^\d+$/.test(node)) return { type: "node", id: `node-${node}` };
  if (hash === "#all") return { type: "all", id: "all" };
  return { type: "overview", id: "overview" };
}

export function serializeGraphHash(selection: GraphSelection): string {
  if (selection.type === "overview") return "#overview";
  if (selection.type === "all") return "#all";
  if (selection.type === "question") return `#question=${selection.id}`;
  const publicId = selection.id.replace(/^[^-]+-/, "");
  return `#${selection.type}=${publicId}`;
}

export function deriveVisibleGraph(graph: GraphCore, selection: GraphSelection): GraphCore {
  if (selection.type === "all") return graph;
  if (selection.type === "overview") return deriveOverviewGraph(graph);

  const visibleIds = new Set<string>();
  const nodeById = new Map(graph.nodes.map((node) => [node.id, node]));

  const addKnowledgeContext = (nodeId: string): void => {
    const node = nodeById.get(nodeId);
    if (!node || node.kind !== "knowledge") return;
    visibleIds.add(node.id);
    if (node.submoduleId) visibleIds.add(node.submoduleId);
    if (node.moduleId) visibleIds.add(node.moduleId);
  };

  if (selection.type === "question") {
    visibleIds.add(selection.id);
    for (const edge of graph.edges) {
      if (edge.kind === "addresses" && edge.source === selection.id) {
        addKnowledgeContext(edge.target);
      }
    }
  }

  if (selection.type === "stage") {
    const stage = graph.stages.find((candidate) => candidate.id === selection.id);
    for (const questionId of stage?.questionIds ?? []) {
      visibleIds.add(questionId);
      for (const edge of graph.edges) {
        if (edge.kind === "addresses" && edge.source === questionId) {
          addKnowledgeContext(edge.target);
        }
      }
    }
  }

  if (selection.type === "module") {
    visibleIds.add(selection.id);
    const knowledgeIds = new Set<string>();
    for (const node of graph.nodes) {
      if (node.moduleId !== selection.id) continue;
      visibleIds.add(node.id);
      if (node.kind === "knowledge") knowledgeIds.add(node.id);
    }
    for (const edge of graph.edges) {
      if (edge.kind === "addresses" && knowledgeIds.has(edge.target)) {
        visibleIds.add(edge.source);
      }
    }
  }

  if (selection.type === "submodule") {
    const knowledgeIds = new Set<string>();
    visibleIds.add(selection.id);
    const selectedSubmodule = nodeById.get(selection.id);
    if (selectedSubmodule?.moduleId) visibleIds.add(selectedSubmodule.moduleId);
    for (const node of graph.nodes) {
      if (node.submoduleId !== selection.id || node.kind !== "knowledge") continue;
      visibleIds.add(node.id);
      knowledgeIds.add(node.id);
    }
    for (const edge of graph.edges) {
      if (edge.layer === "mainline" && knowledgeIds.has(edge.target)) visibleIds.add(edge.source);
      if (edge.layer !== "semantic") continue;
      const neighborId = knowledgeIds.has(edge.source)
        ? edge.target
        : knowledgeIds.has(edge.target)
          ? edge.source
          : undefined;
      if (neighborId) addKnowledgeContext(neighborId);
    }
  }

  if (selection.type === "node") {
    addKnowledgeContext(selection.id);
    for (const edge of graph.edges) {
      if (edge.kind === "addresses" && edge.target === selection.id) {
        visibleIds.add(edge.source);
      }
      if (edge.layer === "semantic" && edge.source === selection.id) {
        addKnowledgeContext(edge.target);
      }
      if (edge.layer === "semantic" && edge.target === selection.id) {
        addKnowledgeContext(edge.source);
      }
    }
  }

  const nodes = graph.nodes.filter((node) => visibleIds.has(node.id));
  const edges = graph.edges.filter(
    (edge) => visibleIds.has(edge.source) && visibleIds.has(edge.target),
  );
  return { ...graph, nodes, edges };
}

export function deriveOverviewGraph(graph: GraphCore): GraphCore {
  const nodes = graph.nodes.filter((node) => node.kind !== "knowledge");
  const visibleIds = new Set(nodes.map((node) => node.id));
  const nodeById = new Map(graph.nodes.map((node) => [node.id, node]));
  const structureEdges = graph.edges.filter(
    (edge) =>
      edge.layer === "structure" &&
      visibleIds.has(edge.source) &&
      visibleIds.has(edge.target),
  );

  const aggregated = new Map<string, GraphCore["edges"][number]>();
  const addAggregated = (
    source: string,
    target: string,
    kind: GraphCore["edges"][number]["kind"],
    layer: GraphCore["edges"][number]["layer"],
    explanation: string,
  ) => {
    if (source === target) return;
    const key = `${layer}|${kind}|${source}|${target}`;
    const current = aggregated.get(key);
    if (current) {
      current.weight = (current.weight ?? 1) + 1;
      current.rationale = `${current.weight} 条${explanation}`;
      return;
    }
    aggregated.set(key, {
      id: `aggregate-${key.replaceAll("|", "-")}`,
      source,
      target,
      kind,
      layer,
      weight: 1,
      rationale: `1 条${explanation}`,
    });
  };

  // The overview uses the curated 45-question route. Teaching-unit tags remain
  // available in focused views, but showing all of them here creates a hairball.
  for (const question of graph.questions) {
    const submoduleCounts = new Map<string, number>();
    for (const nodeNumber of question.nodeNumbers) {
      const submoduleId = nodeById.get(`node-${nodeNumber}`)?.submoduleId;
      if (submoduleId) submoduleCounts.set(submoduleId, (submoduleCounts.get(submoduleId) ?? 0) + 1);
    }
    const primarySubmodule = [...submoduleCounts.entries()]
      .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))[0]?.[0];
    if (primarySubmodule) {
      addAggregated(question.id, primarySubmodule, "addresses", "mainline", "主线问题的主要知识归属");
    }
  }

  for (const edge of graph.edges) {
    if (edge.layer === "semantic") {
      const sourceSubmodule = nodeById.get(edge.source)?.submoduleId;
      const targetSubmodule = nodeById.get(edge.target)?.submoduleId;
      if (sourceSubmodule && targetSubmodule) {
        addAggregated(
          sourceSubmodule,
          targetSubmodule,
          edge.kind,
          "semantic",
          "已审核知识关系",
        );
      }
    }
  }

  return {
    ...graph,
    nodes,
    edges: [...structureEdges, ...aggregated.values()],
  };
}

function publicSearchId(node: GraphNodeRecord): string {
  if (node.kind === "knowledge") return String(node.number ?? "");
  if (node.kind === "question") return node.id;
  if (node.kind === "submodule") return node.code ?? "";
  if (node.kind === "module") return String(node.number ?? "");
  return "";
}

export function searchGraph(graph: GraphCore, query: string, limit = 24): GraphNodeRecord[] {
  const normalized = query.trim().toLocaleLowerCase("zh-CN");
  if (!normalized) return [];
  const kindPriority: Record<GraphNodeRecord["kind"], number> = {
    knowledge: 0,
    question: 1,
    submodule: 2,
    module: 3,
  };

  return graph.nodes
    .map((node) => {
      const publicId = publicSearchId(node).toLocaleLowerCase("zh-CN");
      const label = node.label.toLocaleLowerCase("zh-CN");
      let score = Number.POSITIVE_INFINITY;
      if (publicId === normalized) score = kindPriority[node.kind];
      else if (label === normalized) score = 10 + kindPriority[node.kind];
      else if (label.startsWith(normalized)) score = 20 + kindPriority[node.kind];
      else if (publicId.startsWith(normalized)) score = 30 + kindPriority[node.kind];
      else if (label.includes(normalized)) score = 40 + kindPriority[node.kind];
      return { node, score };
    })
    .filter((entry) => Number.isFinite(entry.score))
    .sort((left, right) => left.score - right.score || left.node.label.localeCompare(right.node.label, "zh-CN"))
    .slice(0, limit)
    .map((entry) => entry.node);
}
